import React, { useState, useEffect } from 'react';
import { 
  FiShield, FiSearch, FiFilter, FiUser, FiCheckCircle, 
  FiCalendar, FiGrid, FiEye, FiDownload
} from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading } from '../../components/common';
import { getAllDocumentVerifications, getAllUsers } from '../../firebase/users';
import { getAllCompanies } from '../../firebase/companies';

const DocumentVerifications = () => {
  const [loading, setLoading] = useState(true);
  const [verifications, setVerifications] = useState([]);
  const [companies, setCompanies] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterDocumentType, setFilterDocumentType] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [verificationsResult, companiesResult, usersResult] = await Promise.all([
        getAllDocumentVerifications(),
        getAllCompanies(),
        getAllUsers()
      ]);

      // Create company lookup map
      const companyMap = {};
      (companiesResult.companies || []).forEach(company => {
        companyMap[company.id] = company;
      });
      setCompanies(companyMap);

      // Create verifier to company map (for managers)
      const verifierCompanyMap = {};
      (usersResult.users || []).forEach(user => {
        if (user.companyId && user.role === 'manager') {
          verifierCompanyMap[user.id] = user.companyId;
        }
      });

      // Enhance verifications with company names from verifier profiles
      const enhancedVerifications = (verificationsResult.verifications || []).map(verification => {
        // If company ID is missing but we can find it from verifier's profile
        if (!verification.verifiedByCompanyId && verification.verifiedBy && verifierCompanyMap[verification.verifiedBy]) {
          const companyId = verifierCompanyMap[verification.verifiedBy];
          return {
            ...verification,
            verifiedByCompanyId: companyId,
            verifiedByCompanyName: companyMap[companyId]?.name || verification.verifiedByCompanyName || 'Unknown'
          };
        }
        // If company ID exists but name is missing/Unknown, look it up
        if (verification.verifiedByCompanyId && 
            (!verification.verifiedByCompanyName || verification.verifiedByCompanyName === 'Unknown')) {
          return {
            ...verification,
            verifiedByCompanyName: companyMap[verification.verifiedByCompanyId]?.name || 'Unknown'
          };
        }
        return verification;
      });

      setVerifications(enhancedVerifications);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter verifications
  const filteredVerifications = verifications.filter(verification => {
    const matchesSearch = 
      verification.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      verification.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      verification.verifiedByName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      verification.bookingNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCompany = filterCompany === 'all' || 
      verification.verifiedByCompanyId === filterCompany;
    
    const matchesDocumentType = filterDocumentType === 'all' || 
      verification.documentType === filterDocumentType;
    
    const matchesRole = filterRole === 'all' || 
      verification.verifiedByRole === filterRole;

    return matchesSearch && matchesCompany && matchesDocumentType && matchesRole;
  });

  const handleViewDocument = (verification) => {
    setSelectedVerification(verification);
    setShowDocumentModal(true);
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'license-front': 'License Front',
      'license-back': 'License Back',
      'aadhar-front': 'Aadhar Front'
    };
    return labels[type] || type;
  };

  const getRoleBadge = (role) => {
    const colors = {
      'admin': 'bg-purple-100 text-purple-700',
      'manager': 'bg-blue-100 text-blue-700',
      'user': 'bg-gray-100 text-gray-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <AdminLayout title="Document Verifications">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Document Verifications">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Document Verification Logs</h2>
        <p className="text-gray-600 mt-1">
          Track all document verifications across the platform for transparency and security
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FiShield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Verifications</p>
              <p className="text-xl font-bold text-gray-900">{verifications.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FiCheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Unique Users</p>
              <p className="text-xl font-bold text-gray-900">
                {new Set(verifications.map(v => v.userId)).size}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FiGrid className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Companies Involved</p>
              <p className="text-xl font-bold text-gray-900">
                {new Set(verifications.map(v => v.verifiedByCompanyId).filter(Boolean)).size}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FiUser className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Managers</p>
              <p className="text-xl font-bold text-gray-900">
                {verifications.filter(v => v.verifiedByRole === 'manager').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user, verifier, booking..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company
            </label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Companies</option>
              {Object.values(companies).map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type
            </label>
            <select
              value={filterDocumentType}
              onChange={(e) => setFilterDocumentType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="license-front">License Front</option>
              <option value="license-back">License Back</option>
              <option value="aadhar-front">Aadhar Front</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verified By
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Verifications List */}
      {filteredVerifications.length > 0 ? (
        <div className="space-y-4">
          {filteredVerifications.map((verification) => (
            <Card key={verification.id} className="p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {verification.userName}
                      </h3>
                      <p className="text-sm text-gray-600">{verification.userEmail}</p>
                    </div>
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                      {getDocumentTypeLabel(verification.documentType)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center gap-2 text-sm">
                      <FiUser className="text-gray-400" size={16} />
                      <span className="text-gray-600">Verified by:</span>
                      <span className="font-medium text-gray-900">
                        {verification.verifiedByName}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getRoleBadge(verification.verifiedByRole)}`}>
                        {verification.verifiedByRole}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <FiGrid className="text-gray-400" size={16} />
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium text-gray-900">
                        {verification.verifiedByCompanyName || 
                         (verification.verifiedByCompanyId && companies[verification.verifiedByCompanyId]?.name) || 
                         'Unknown'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <FiCalendar className="text-gray-400" size={16} />
                      <span className="text-gray-600">Verified at:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(verification.verifiedAt).toLocaleString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>

                    {verification.bookingNumber && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">Booking:</span>
                        <span className="font-medium text-gray-900">
                          {verification.bookingNumber}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDocument(verification)}
                  >
                    <FiEye className="mr-2" size={14} />
                    View Document
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <FiShield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || filterCompany !== 'all' || filterDocumentType !== 'all' || filterRole !== 'all'
              ? 'No verifications found'
              : 'No verifications yet'}
          </h3>
          <p className="text-gray-600">
            {searchQuery || filterCompany !== 'all' || filterDocumentType !== 'all' || filterRole !== 'all'
              ? 'Try adjusting your filters'
              : 'Document verifications will appear here once managers verify customer documents'}
          </p>
        </Card>
      )}

      {/* Document Modal */}
      {showDocumentModal && selectedVerification && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Document Details</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDocumentModal(false);
                  setSelectedVerification(null);
                }}
              >
                Close
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">User:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {selectedVerification.userName}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Document Type:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {getDocumentTypeLabel(selectedVerification.documentType)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Verified By:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {selectedVerification.verifiedByName} ({selectedVerification.verifiedByRole})
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Company:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {selectedVerification.verifiedByCompanyName || 
                     (selectedVerification.verifiedByCompanyId && companies[selectedVerification.verifiedByCompanyId]?.name) || 
                     'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Verified At:</span>
                  <span className="font-medium text-gray-900 ml-2">
                    {new Date(selectedVerification.verifiedAt).toLocaleString('en-IN')}
                  </span>
                </div>
                {selectedVerification.bookingNumber && (
                  <div>
                    <span className="text-gray-600">Booking:</span>
                    <span className="font-medium text-gray-900 ml-2">
                      {selectedVerification.bookingNumber}
                    </span>
                  </div>
                )}
              </div>

              {selectedVerification.documentUrl && (
                <div>
                  <img
                    src={selectedVerification.documentUrl}
                    alt={getDocumentTypeLabel(selectedVerification.documentType)}
                    className="w-full h-auto rounded-lg border border-gray-200"
                  />
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default DocumentVerifications;
